import {
  accountCompanyInfoAtom,
  accountListStateAtom,
  companyAuthSettingsAtom,
  selectedAccountStateAtom,
  userAccountAtom,
} from '../models/atoms/account_atoms';
import { authStateAtom } from '../models/atoms/auth_atoms';
import { getRecoil, setRecoil } from 'recoil-nexus';
import {
  accountInterface,
  accountListInterface,
  createAccountInterface,
  accountCompanyInfoInterface,
  AccountAlert,
  companyAuthSettingsInterface,
} from '../models/interfaces/account_interfaces';
import { defaultLocationAtom } from '../models/atoms/ui_atoms';
import {
  type Language,
  LANGUAGE_STORAGE_KEY,
  LAST_ACCOUNT_STORAGE_KEY,
} from '../contexts/TranslationContext';

const auth_link = import.meta.env.REACT_APP_AUTH_API_URL;

export const account_controller = {
  get_account_list: function () {
    const obj = {
      link: auth_link + '/accounts/accounts',
      object: {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
        },
      },
    };
    fetch(obj.link, obj.object)
      .then(async response => (await response.json()) as accountListInterface)
      .then(accountList => {
        console.log(accountList);
        setRecoil(accountListStateAtom, accountList);
      })
      .catch(e => {
        console.error(e);
        setRecoil(accountListStateAtom, { accounts: [] });
      });
  },

  create_account: async function (
    email: string,
    view_devices: boolean | string[],
    register_devices: boolean,
    manage_devices: boolean,
    manage_accounts: boolean,
    first_name: string,
    last_name: string,
    phone_number: string,
  ) {
    if (Array.isArray(view_devices)) {
      view_devices = Array.from(new Set(view_devices)).sort();
    }
    const new_account: createAccountInterface = {
      email: email,
      first_name: first_name,
      last_name: last_name,
      phone_number: phone_number,
      permissions: {
        view_devices: view_devices,
        register_devices: register_devices,
        manage_devices: manage_devices,
        manage_accounts: manage_accounts,
      },
    };
    const obj = {
      link: auth_link + '/accounts/account',
      object: {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + getRecoil(authStateAtom).token,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(new_account),
      },
    };

    try {
      const res = await fetch(obj.link, obj.object);
      if (!res.ok) {
        const errorMessage = await res.json();

        throw new Error('Failed to create account', {
          cause: errorMessage.field,
        });
      }
      this.get_account_list();
    } catch (e) {
      console.error(e);
      if (e instanceof Error && e.message === 'Failed to create account') {
        throw e;
      }
      throw new Error('Failed to create account', { cause: e });
    }
  },

  select_account: function (account_id: string, callback = () => {}) {
    const obj = {
      link: auth_link + '/accounts/account/' + account_id,
      object: {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
        },
      },
    };
    fetch(obj.link, obj.object)
      .then(async response => (await response.json()) as accountInterface)
      .then(account => {
        setRecoil(selectedAccountStateAtom, account);
        return account;
      })
      .then(() => callback());
  },

  delete_account: function (account_id: string) {
    const obj = {
      link: auth_link + '/accounts/account/' + account_id,
      object: {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
        },
      },
    };
    fetch(obj.link, obj.object).then(() => this.get_account_list());
  },

  modify_account: function (
    account_id: string,
    view_devices: boolean | string[],
    register_devices: boolean,
    manage_devices: boolean,
    manage_accounts: boolean,
  ) {
    if (Array.isArray(view_devices)) {
      view_devices = Array.from(new Set(view_devices)).sort();
    }
    const new_account = {
      id: account_id,
      permissions: {
        view_devices: view_devices,
        register_devices: register_devices,
        manage_devices: manage_devices,
        manage_accounts: manage_accounts,
      },
    };
    const obj = {
      link: auth_link + '/accounts/account',
      object: {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + getRecoil(authStateAtom).token,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(new_account),
      },
    };
    fetch(obj.link, obj.object).then(() => {
      this.get_account_list();
    });
  },

  get_user_account: function (setDefaultLanguage?: (lang: Language) => void) {
    const obj = {
      link: auth_link + '/accounts/user_account',
      object: {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
        },
      },
    };
    fetch(obj.link, obj.object)
      .then(async response => (await response.json()) as accountInterface)
      .then(account => {
        const previousAccount = localStorage.getItem(LAST_ACCOUNT_STORAGE_KEY);
        if (previousAccount && previousAccount !== account.id) {
          localStorage.removeItem(LANGUAGE_STORAGE_KEY);
        }
        localStorage.setItem(LAST_ACCOUNT_STORAGE_KEY, account.id);

        let language: Language | undefined = undefined;
        // Default to en-GB if no language is set or langauge is 'en', which used to be the default in schemas
        if (account.language === 'en') {
          language = 'en-GB';
        } else {
          language = account.language as Language | undefined;
        }
        if (language) {
          setDefaultLanguage?.(language);
        }
        if (account.default_location) {
          setRecoil(defaultLocationAtom, account.default_location);
        }

        setRecoil(userAccountAtom, account);
      })
      .catch(e => {
        console.error(e);
        this.get_account_list();
      });
  },

  modify_user_account: function (user_patch: {
    email?: string;
    phone_number?: string;
    text_alerts?: boolean;
    email_alerts?: boolean;
    alerts?: AccountAlert;
    first_name?: string;
    last_name?: string;
  }) {
    const obj = {
      link: auth_link + '/accounts/user_account',
      object: {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + getRecoil(authStateAtom).token,
          'Content-type': 'application/json',
        },
        body: JSON.stringify(user_patch),
      },
    };
    console.log('user_patch', user_patch);
    fetch(obj.link, obj.object).then(() => {
      console.log('getting user account after patch');
      this.get_user_account();
      this.get_account_list();
    });
  },

  change_account_password: function (account_id: string, new_password: string) {
    fetch(`${auth_link}/accounts/password`, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${getRecoil(authStateAtom).token}`,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({
        account_id: account_id,
        new_password: new_password,
      }),
    });
  },

  change_user_password: async function (
    new_password: string,
    current_password: string,
  ) {
    const response = await fetch(`${auth_link}/accounts/user_password`, {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${getRecoil(authStateAtom).token}`,
        'Content-type': 'application/json',
      },
      body: JSON.stringify({ current_password, new_password }),
    });
    if (response.ok) {
      return response;
    } else {
      throw await response.json();
    }
  },

  get_company_info: function () {
    const obj = {
      link: auth_link + '/account/company_info',
      object: {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
        },
      },
    };
    fetch(obj.link, obj.object)
      .then(
        async response =>
          (await response.json()) as accountCompanyInfoInterface,
      )
      .then(company_info => setRecoil(accountCompanyInfoAtom, company_info));
  },

  send_password_link: function (email: string, callback: () => void) {
    const obj = {
      link:
        auth_link +
        '/accounts/password_link?' +
        new URLSearchParams({
          email: email,
        }),
      object: {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      },
    };
    fetch(obj.link, obj.object).then(response => {
      if (response.status === 200) {
        callback();
      }
    });
  },

  get_company_auth_settings: function () {
    const obj = {
      link: auth_link + '/company/auth_settings',
      object: {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + getRecoil(authStateAtom).token,
        },
      },
    };
    fetch(obj.link, obj.object)
      .then(
        async response =>
          (await response.json()) as companyAuthSettingsInterface,
      )
      .then(company_info => setRecoil(companyAuthSettingsAtom, company_info));
  },
};
